import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  createMeetingSession,
  deleteMeetingSession,
  getMeetingSession,
  listMeetingSessions,
  listMeetingTreeClients,
} from "../../api/Meetings/meetingSessionsApi";
import MeetingBrandSelect from "./MeetingBrandSelect";
import MeetingAttendeeSelect from "./MeetingAttendeeSelect";
import MeetingClientContactSelect from "./MeetingClientContactSelect";
import MeetingDeleteDialog from "./MeetingDeleteDialog";
import MeetingConsentPanel from "./MeetingConsentPanel";
import MeetingSessionPage from "./MeetingSessionPage";
import {
  dashGlassCardSx,
  dashHeroSx,
  dashToolbarBtnSx,
} from "../MyDashboard/myDashboardStyles";
import {
  meetingDetailPaneSx,
  meetingListPaneSx,
  meetingListSplitSx,
  meetingListTreeBodySx,
  meetingPageShellSx,
  meetingStickyHeaderSx,
} from "./meetingUiStyles";
import {
  formatMeetingPhotoMaxMb,
  formatMeetingVideoMaxMb,
  formatMeetingMediaWarnMb,
  isMeetingConsentComplete,
  MEETING_MEDIA_RETENTION_DAYS,
  statusLabelKey,
  toDateInputValue,
} from "../../utils/meetingRecorder";
import {
  buildMeetingTreeGroups,
  filterMeetingSessions,
  formatMeetingListDate,
  meetingTreeCustomerKey,
  mergeMeetingSessionRows,
  uniqueMeetingCustomers,
} from "../../utils/meetingListUtils";
import { jwtDecode } from "jwt-decode";

const TREE_COLLAPSE_KEY = "meetings.treeCollapsed";
const CAPTURE_RAIL_STORAGE_KEY = "crm.meeting.captureRailOpen";

function readCaptureParked() {
  try {
    return sessionStorage.getItem(CAPTURE_RAIL_STORAGE_KEY) === "0";
  } catch {
    return false;
  }
}

function readTreeCollapsed() {
  try {
    return localStorage.getItem(TREE_COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

const emptyConsent = () => ({
  consentClientsInformed: false,
  consentRecordingAccepted: false,
  consentAiAccepted: false,
});

const statusColor = {
  recording: "warning",
  processing: "info",
  ready: "success",
  failed: "error",
};

function currentTokenPayload() {
  try {
    return jwtDecode(localStorage.getItem("token") || "");
  } catch {
    return {};
  }
}

function currentUserId() {
  const payload = currentTokenPayload();
  const n = Number(payload.userId ?? payload.id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function statusDotColor(theme, status) {
  const key = statusColor[status];
  if (key && theme.palette[key]?.main) return theme.palette[key].main;
  return theme.palette.text.disabled;
}

function MeetingMediaCountChip({ icon, count, color, title }) {
  return (
    <Chip
      size="small"
      icon={icon}
      label={String(count)}
      title={title}
      sx={{
        height: 20,
        maxWidth: "100%",
        flexShrink: 0,
        borderColor: alpha(color, 0.45),
        bgcolor: alpha(color, 0.12),
        color,
        "& .MuiChip-icon": {
          color,
          ml: 0.35,
          mr: -0.35,
          fontSize: 13,
        },
        "& .MuiChip-label": {
          px: 0.45,
          fontSize: "0.68rem",
          fontWeight: 700,
          lineHeight: 1.2,
        },
      }}
      variant="outlined"
    />
  );
}

/** Fila compacta del árbol: título + chips; fecha debajo (sin desbordar). */
function MeetingTreeLeaf({
  row,
  selected,
  t,
  theme,
  locale,
  onSelect,
  onOpen,
}) {
  const dateLabel = formatMeetingListDate(row.meetingDate, locale) || "—";
  const audio = row.audioCount || 0;
  const photos = row.imageCount ?? row.photoCount ?? 0;
  const videos = row.videoCount || 0;
  const audioColor = theme.palette.warning.dark;
  const photoColor = theme.palette.primary.main;
  const videoColor = theme.palette.secondary.main;
  return (
    <Box
      role="treeitem"
      aria-selected={selected}
      onClick={() => onSelect(row)}
      onDoubleClick={() => onOpen(row)}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 0.75,
        minHeight: 44,
        maxWidth: "100%",
        minWidth: 0,
        flexShrink: 0,
        boxSizing: "border-box",
        pl: 1,
        pr: 0.75,
        py: 0.55,
        cursor: "pointer",
        borderRadius: 1.25,
        bgcolor: selected
          ? alpha(theme.palette.primary.main, 0.1)
          : "transparent",
        "&:hover": {
          bgcolor: selected
            ? alpha(theme.palette.primary.main, 0.14)
            : alpha(theme.palette.action.hover, 0.6),
        },
      }}
    >
      <Box
        aria-hidden
        title={t(statusLabelKey(row.status))}
        sx={{
          width: 8,
          height: 8,
          mt: 0.7,
          borderRadius: "50%",
          flexShrink: 0,
          bgcolor: statusDotColor(theme, row.status),
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        <Typography
          noWrap
          sx={{
            fontWeight: selected ? 700 : 600,
            fontSize: "0.875rem",
            lineHeight: 1.25,
          }}
        >
          {row.title || t("Meetings.untitled")}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.45}
          sx={{ mt: 0.2, minWidth: 0, width: "100%" }}
        >
          <Typography
            noWrap
            variant="caption"
            color="text.secondary"
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              lineHeight: 1.2,
              fontWeight: 600,
            }}
          >
            {dateLabel}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.35}
            sx={{ flexShrink: 0 }}
          >
            <MeetingMediaCountChip
              color={audioColor}
              count={audio}
              title={t("Meetings.record")}
              icon={<MicNoneOutlinedIcon />}
            />
            <MeetingMediaCountChip
              color={photoColor}
              count={photos}
              title={t("Meetings.photo")}
              icon={<PhotoCameraOutlinedIcon />}
            />
            <MeetingMediaCountChip
              color={videoColor}
              count={videos}
              title={t("Meetings.video")}
              icon={<VideocamOutlinedIcon />}
            />
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

function MeetingTreeGroup({
  group,
  expanded,
  onToggle,
  selectedId,
  t,
  theme,
  locale,
  onSelect,
  onOpen,
}) {
  const label = group.customerName || t("Meetings.noCustomer");
  const count =
    group.sessionCount != null ? group.sessionCount : group.meetings.length;
  const loading = Boolean(group.loading);
  return (
    <Box
      role="treeitem"
      aria-expanded={expanded}
      sx={{
        minWidth: 0,
        maxWidth: "100%",
        flexShrink: 0,
        // Altura natural: el cuerpo del árbol hace scroll.
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          minHeight: 40,
          minWidth: 0,
          px: 0.75,
          py: 0.35,
          cursor: "pointer",
          borderRadius: 1.25,
          userSelect: "none",
          bgcolor: (th) =>
            th.palette.mode === "dark"
              ? alpha(th.palette.common.white, 0.06)
              : alpha(th.palette.text.primary, 0.05),
          "&:hover": {
            bgcolor: (th) =>
              th.palette.mode === "dark"
                ? alpha(th.palette.common.white, 0.1)
                : alpha(th.palette.text.primary, 0.08),
          },
        }}
      >
        {expanded ? (
          <ExpandMoreOutlinedIcon
            sx={{ fontSize: 20, color: "text.secondary" }}
          />
        ) : (
          <ChevronRightOutlinedIcon
            sx={{ fontSize: 20, color: "text.secondary" }}
          />
        )}
        <Typography
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 800,
            fontSize: "0.8125rem",
            letterSpacing: 0.01,
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
        {loading ? (
          <CircularProgress size={14} thickness={5} sx={{ flexShrink: 0 }} />
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              flexShrink: 0,
              fontWeight: 700,
              minWidth: 22,
              textAlign: "right",
            }}
          >
            {count}
          </Typography>
        )}
      </Box>
      {/* Sin Collapse: con lazy-load la altura cambia al hidratar y Collapse se queda a 0. */}
      {expanded ? (
        <Stack
          role="group"
          spacing={0}
          sx={{
            pb: 0.5,
            ml: 0.5,
            minWidth: 0,
            maxWidth: "100%",
            boxSizing: "border-box",
            // Solo raya fina debajo entre reuniones (sin guía vertical)
            "& > *:not(:last-of-type)": {
              borderBottom: "1px solid",
              borderColor: (th) =>
                th.palette.mode === "dark"
                  ? alpha(th.palette.common.white, 0.08)
                  : alpha(th.palette.text.primary, 0.08),
            },
          }}
        >
          {loading && !group.meetings.length ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 1.25, py: 0.75 }}
            >
              <CircularProgress size={14} thickness={5} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                {t("Meetings.loading")}
              </Typography>
            </Stack>
          ) : null}
          {group.meetings.map((row) => (
            <MeetingTreeLeaf
              key={row.id}
              row={row}
              selected={Number(selectedId) === Number(row.id)}
              t={t}
              theme={theme}
              locale={locale}
              onSelect={onSelect}
              onOpen={onOpen}
            />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

export default function MeetingsPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const dateLocale = i18n.language || "es-ES";
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const routeId = useMemo(() => {
    const m = String(pathname || "").match(/^\/reuniones\/(\d+)\b/);
    return m ? m[1] : null;
  }, [pathname]);
  const { contentMargin } = useContext(SidebarContext);
  const [treeClients, setTreeClients] = useState([]);
  const [rows, setRows] = useState([]);
  const [hydratedKeys, setHydratedKeys] = useState(() => new Set());
  const [loadingKeys, setLoadingKeys] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [meetingDate, setMeetingDate] = useState(() => toDateInputValue());
  const [attendeeIds, setAttendeeIds] = useState([]);
  const [clientContactIds, setClientContactIds] = useState([]);
  const [consent, setConsent] = useState(emptyConsent);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [query, setQuery] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedId, setSelectedId] = useState(() =>
    routeId && Number.isFinite(Number(routeId)) ? Number(routeId) : null,
  );
  /** Claves de cliente expandidas en el árbol ("" = sin cliente). Cerrado por defecto. */
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [captureParked, setCaptureParked] = useState(readCaptureParked);
  const [captureSlotEl, setCaptureSlotEl] = useState(null);
  const unparkCaptureRef = useRef(null);
  const captureParkedRef = useRef(false);
  captureParkedRef.current = captureParked;
  const [treeCollapsed, setTreeCollapsed] = useState(
    () => readTreeCollapsed() || readCaptureParked(),
  );
  const treeCollapsedRef = useRef(treeCollapsed);
  treeCollapsedRef.current = treeCollapsed;
  const hydratedKeysRef = useRef(hydratedKeys);
  hydratedKeysRef.current = hydratedKeys;
  const loadingKeysRef = useRef(loadingKeys);
  loadingKeysRef.current = loadingKeys;
  const expandedKeysRef = useRef(expandedKeys);
  expandedKeysRef.current = expandedKeys;
  const deepLinkTriedRef = useRef(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const clients = await listMeetingTreeClients();
      setTreeClients(Array.isArray(clients) ? clients : []);
      setRows([]);
      setHydratedKeys(new Set());
      // No tocar selectedId: lo manda la URL; así el refresh no desmonta la ficha.
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Meetings.loadError"),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  const hydrateCustomer = useCallback(
    async (customerNameKey) => {
      const key = String(customerNameKey || "").trim();
      if (hydratedKeysRef.current.has(key)) return;
      if (loadingKeysRef.current.has(key)) return;
      setLoadingKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      try {
        const data = await listMeetingSessions({ customerName: key });
        const list = Array.isArray(data) ? data : [];
        setRows((prev) => mergeMeetingSessionRows(prev, list));
        setHydratedKeys((prev) => {
          const next = new Set(prev);
          next.add(key);
          return next;
        });
      } catch (err) {
        setError(
          err.response?.data?.error || err.message || t("Meetings.loadError"),
        );
      } finally {
        setLoadingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [t],
  );

  useEffect(() => {
    load();
  }, [load]);

  // URL → selección (deep link / click)
  useEffect(() => {
    if (routeId && Number.isFinite(Number(routeId))) {
      setSelectedId(Number(routeId));
      return;
    }
    if (!routeId) setSelectedId(null);
  }, [routeId]);

  // Deep link: si la sesión no está en rows, pedir ficha y hidratar su cliente
  useEffect(() => {
    if (!routeId || !Number.isFinite(Number(routeId))) return undefined;
    const idNum = Number(routeId);
    if (rows.some((r) => Number(r.id) === idNum)) return undefined;
    if (deepLinkTriedRef.current.has(idNum)) return undefined;
    deepLinkTriedRef.current.add(idNum);
    let cancelled = false;
    (async () => {
      try {
        const session = await getMeetingSession(idNum);
        if (cancelled || !session?.id) return;
        setRows((prev) => mergeMeetingSessionRows(prev, [session]));
        const key = meetingTreeCustomerKey(session);
        setTreeClients((prev) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          if (list.some((c) => String(c?.customerName || "").trim() === key)) {
            return list;
          }
          return [...list, { customerName: key, sessionCount: 1 }];
        });
        setExpandedKeys((prev) => {
          if (prev.has(key)) return prev;
          const next = new Set(prev);
          next.add(key);
          return next;
        });
        // Completar el grupo del cliente (el GET /:id es ficha completa)
        hydrateCustomer(key);
      } catch {
        /* la ficha embebida gestiona 404 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeId, rows, hydrateCustomer]);

  const customers = useMemo(() => {
    const fromRoots = (treeClients || [])
      .map((c) => String(c?.customerName || "").trim())
      .filter(Boolean);
    const fromRows = uniqueMeetingCustomers(rows);
    return [...new Set([...fromRoots, ...fromRows])].sort((a, b) =>
      a.localeCompare(b, "es", { sensitivity: "base" }),
    );
  }, [treeClients, rows]);

  const filtered = useMemo(
    () =>
      filterMeetingSessions(rows, {
        query,
        customer: customerFilter,
        status: statusFilter,
      }),
    [rows, query, customerFilter, statusFilter],
  );
  const groups = useMemo(
    () =>
      buildMeetingTreeGroups(treeClients, rows, {
        query,
        customer: customerFilter,
        status: statusFilter,
        hydratedKeys,
        loadingKeys,
      }),
    [
      treeClients,
      rows,
      query,
      customerFilter,
      statusFilter,
      hydratedKeys,
      loadingKeys,
    ],
  );
  const selected = useMemo(
    () =>
      rows.find((r) => Number(r.id) === Number(selectedId)) ||
      filtered.find((r) => Number(r.id) === Number(selectedId)) ||
      null,
    [rows, filtered, selectedId],
  );

  // Solo auto-abre el cliente de la reunión activa (grupos cerrados por defecto)
  useEffect(() => {
    if (!selected) return;
    const key = meetingTreeCustomerKey(selected);
    setExpandedKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    hydrateCustomer(key);
  }, [selected, hydrateCustomer]);

  // Búsqueda / filtros: pedir listado filtrado y expandir grupos visibles
  useEffect(() => {
    const q = query.trim();
    const forceOpen = Boolean(q || customerFilter || statusFilter);
    if (!forceOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const params = {};
        if (q) params.q = q;
        if (customerFilter) params.customerName = customerFilter;
        const data = await listMeetingSessions(params);
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        const statusFiltered = statusFilter
          ? list.filter((r) => String(r.status || "") === statusFilter)
          : list;
        setRows((prev) => mergeMeetingSessionRows(prev, statusFiltered));
        const keys = new Set(
          statusFiltered.map((r) => meetingTreeCustomerKey(r)),
        );
        setHydratedKeys((prev) => {
          const next = new Set(prev);
          keys.forEach((k) => next.add(k));
          return next;
        });
        setExpandedKeys(keys);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error || err.message || t("Meetings.loadError"),
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [query, customerFilter, statusFilter, t]);

  const toggleGroup = useCallback(
    (customerNameKey) => {
      const key = String(customerNameKey || "").trim();
      const willOpen = !expandedKeysRef.current.has(key);
      setExpandedKeys((prev) => {
        const next = new Set(prev);
        if (willOpen) next.add(key);
        else next.delete(key);
        return next;
      });
      // Fuera del updater (Strict Mode lo ejecutaría 2× y podría liar el expand).
      if (willOpen) hydrateCustomer(key);
    },
    [hydrateCustomer],
  );

  const toggleTreeCollapsed = useCallback(() => {
    const prev = treeCollapsedRef.current;
    const next = !prev;
    if (prev && captureParkedRef.current) {
      unparkCaptureRef.current?.();
    }
    treeCollapsedRef.current = next;
    setTreeCollapsed(next);
    try {
      localStorage.setItem(TREE_COLLAPSE_KEY, next ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, []);

  const onCaptureRailChange = useCallback((open) => {
    setCaptureParked(!open);
    if (!open) {
      treeCollapsedRef.current = true;
      setTreeCollapsed(true);
      try {
        localStorage.setItem(TREE_COLLAPSE_KEY, "1");
      } catch {
        /* ignore */
      }
    }
  }, []);

  const registerUnparkCapture = useCallback((fn) => {
    unparkCaptureRef.current = typeof fn === "function" ? fn : null;
  }, []);

  const handleSessionChange = useCallback((patch) => {
    if (patch?.id == null) return;
    setRows((prev) =>
      prev.map((row) =>
        Number(row.id) === Number(patch.id) ? { ...row, ...patch } : row,
      ),
    );
  }, []);

  const openCreate = () => {
    const uid = currentUserId();
    setTitle("");
    setCustomerName("");
    setMeetingDate(toDateInputValue());
    setAttendeeIds(uid ? [uid] : []);
    setClientContactIds([]);
    setConsent(emptyConsent());
    setOpen(true);
  };

  const handleCreate = async () => {
    if (!isMeetingConsentComplete(consent)) {
      setError(t("Meetings.consentRequired"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const session = await createMeetingSession({
        title: title.trim(),
        customerName: customerName.trim(),
        meetingDate,
        attendeeUserIds: attendeeIds,
        clientContactIds,
        ...consent,
      });
      setOpen(false);
      setTitle("");
      setCustomerName("");
      setConsent(emptyConsent());
      if (session?.id) {
        const key = meetingTreeCustomerKey(session);
        setRows((prev) => mergeMeetingSessionRows(prev, [session]));
        setHydratedKeys((prev) => {
          const next = new Set(prev);
          next.add(key);
          return next;
        });
        setTreeClients((prev) => {
          const list = Array.isArray(prev) ? [...prev] : [];
          const idx = list.findIndex(
            (c) => String(c?.customerName || "").trim() === key,
          );
          if (idx >= 0) {
            list[idx] = {
              ...list[idx],
              sessionCount: (Number(list[idx].sessionCount) || 0) + 1,
            };
            return list;
          }
          return [...list, { customerName: key, sessionCount: 1 }];
        });
      }
      navigate(`/reuniones/${session.id}`);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Meetings.createError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    const deletedId = Number(deleteTarget.id);
    setDeleting(true);
    setError("");
    try {
      await deleteMeetingSession(deleteTarget.id);
      setDeleteTarget(null);
      if (Number(selectedId) === deletedId || Number(routeId) === deletedId) {
        navigate("/reuniones");
      }
      await load();
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Meetings.deleteError"),
      );
    } finally {
      setDeleting(false);
    }
  };

  const openMeeting = (row) => {
    if (row?.id) navigate(`/reuniones/${row.id}`);
  };

  const selectMeeting = (row) => {
    if (!row?.id) return;
    setSelectedId(row.id);
    navigate(`/reuniones/${row.id}`, { replace: isDesktop });
  };

  const retentionTooltip = t("Meetings.retentionBanner", {
    days: MEETING_MEDIA_RETENTION_DAYS,
    warnMb: formatMeetingMediaWarnMb(),
    photoMb: formatMeetingPhotoMaxMb(),
    videoMb: formatMeetingVideoMaxMb(),
  });

  const compactFieldSx = {
    "& .MuiOutlinedInput-root": {
      height: 34,
      fontSize: "0.8125rem",
      bgcolor: alpha(theme.palette.background.paper, 0.72),
      borderRadius: 1.5,
      "& fieldset": {
        borderColor: alpha(theme.palette.divider, 0.55),
      },
      "&:hover fieldset": {
        borderColor: alpha(theme.palette.primary.main, 0.4),
      },
    },
    "& .MuiSelect-select": {
      py: 0.75,
      display: "flex",
      alignItems: "center",
    },
  };

  const filtersBar =
    !loading && (treeClients.length > 0 || rows.length > 0) ? (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{
          minWidth: 0,
          flex: { xs: "1 1 100%", md: "1 1 auto" },
          justifyContent: { xs: "stretch", md: "flex-end" },
          flexWrap: "wrap",
          rowGap: 0.75,
        }}
      >
        <TextField
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("Meetings.searchPlaceholder")}
          sx={{
            ...compactFieldSx,
            flex: { xs: "1 1 100%", sm: "1 1 160px", md: "1 1 200px" },
            minWidth: { xs: 0, sm: 140 },
            maxWidth: { md: 260 },
          }}
          InputProps={{
            startAdornment: (
              <SearchOutlinedIcon
                sx={{ mr: 0.75, fontSize: 18, color: "text.secondary" }}
              />
            ),
          }}
        />
        <FormControl
          size="small"
          sx={{ ...compactFieldSx, minWidth: 112, flex: "0 1 auto" }}
        >
          <Select
            displayEmpty
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            renderValue={(v) => v || t("Meetings.filterCustomer")}
          >
            <MenuItem value="">{t("Meetings.filterAll")}</MenuItem>
            {customers.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          size="small"
          sx={{ ...compactFieldSx, minWidth: 104, flex: "0 1 auto" }}
        >
          <Select
            displayEmpty
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            renderValue={(v) => {
              if (!v) return t("Meetings.filterStatus");
              const map = {
                recording: t("Meetings.statusRecording"),
                processing: t("Meetings.statusProcessing"),
                ready: t("Meetings.statusReady"),
                failed: t("Meetings.statusFailed"),
              };
              return map[v] || v;
            }}
          >
            <MenuItem value="">{t("Meetings.filterAll")}</MenuItem>
            <MenuItem value="recording">
              {t("Meetings.statusRecording")}
            </MenuItem>
            <MenuItem value="processing">
              {t("Meetings.statusProcessing")}
            </MenuItem>
            <MenuItem value="ready">{t("Meetings.statusReady")}</MenuItem>
            <MenuItem value="failed">{t("Meetings.statusFailed")}</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    ) : null;

  const listPane = (
    <Paper
      role="tree"
      elevation={0}
      sx={{
        ...meetingListPaneSx,
        ...dashGlassCardSx(theme),
        gap: 0,
        py: 0.75,
        px: treeCollapsed ? 0.5 : 0.75,
        ...(treeCollapsed && captureParked ? { p: 0.5 } : {}),
      }}
    >
      {isDesktop ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={treeCollapsed ? "center" : "space-between"}
          spacing={0.5}
          sx={{
            flexShrink: 0,
            position: "sticky",
            top: 0,
            zIndex: 2,
            px: treeCollapsed ? 0 : 0.5,
            mb: 0.5,
            minHeight: 36,
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 0.75,
            bgcolor:
              theme.palette.mode === "dark" ? "rgba(21,32,48,0.98)" : "#ffffff",
          }}
        >
          {!treeCollapsed ? (
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                lineHeight: 1.2,
                pl: 0.25,
                color: "text.primary",
              }}
            >
              {t("Meetings.treeTitle")}
            </Typography>
          ) : null}
          <Tooltip
            title={
              treeCollapsed
                ? t("Meetings.treeExpand")
                : t("Meetings.treeCollapse")
            }
          >
            <IconButton
              size="small"
              color="primary"
              onClick={toggleTreeCollapsed}
              aria-label={
                treeCollapsed
                  ? t("Meetings.treeExpand")
                  : t("Meetings.treeCollapse")
              }
              sx={{
                border: "1px solid",
                borderColor: "primary.main",
                bgcolor: (th) => alpha(th.palette.primary.main, 0.08),
                "&:hover": {
                  bgcolor: (th) => alpha(th.palette.primary.main, 0.16),
                },
              }}
            >
              {treeCollapsed ? (
                <ChevronRightOutlinedIcon fontSize="small" />
              ) : (
                <ChevronLeftOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
      ) : null}
      <Box
        sx={{
          ...meetingListTreeBodySx,
          ...(treeCollapsed && isDesktop
            ? {
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                "& > * + *": { mt: 0 },
              }
            : null),
        }}
      >
        {treeCollapsed && isDesktop ? (
          captureParked ? (
            <Box
              ref={setCaptureSlotEl}
              sx={{
                flex: 1,
                minHeight: 0,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            />
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                alignSelf: "center",
                mt: 1,
                letterSpacing: 0.4,
                fontWeight: 700,
              }}
            >
              {t("Meetings.title")}
            </Typography>
          )
        ) : (
          groups.map((group) => (
            <MeetingTreeGroup
              key={group.customerName || "__none__"}
              group={group}
              expanded={expandedKeys.has(
                String(group.customerName || "").trim(),
              )}
              onToggle={() => toggleGroup(group.customerName)}
              selectedId={selectedId}
              t={t}
              theme={theme}
              locale={dateLocale}
              onSelect={selectMeeting}
              onOpen={openMeeting}
            />
          ))
        )}
      </Box>
    </Paper>
  );

  const activeSessionId = routeId || selectedId;

  const detailPane = activeSessionId ? (
    <Box sx={meetingDetailPaneSx}>
      <MeetingSessionPage
        key={String(activeSessionId)}
        embedded={isDesktop}
        sessionId={activeSessionId}
        captureSlotEl={captureParked && isDesktop ? captureSlotEl : null}
        onCaptureRailChange={onCaptureRailChange}
        onRegisterUnpark={registerUnparkCapture}
        onSessionChange={handleSessionChange}
      />
    </Box>
  ) : (
    <Paper
      sx={{
        ...dashGlassCardSx(theme),
        ...meetingDetailPaneSx,
        p: 3,
        justifyContent: "center",
      }}
      elevation={0}
    >
      <Typography color="text.secondary">
        {t("Meetings.selectMeetingHint")}
      </Typography>
    </Paper>
  );

  // Móvil: ficha a pantalla completa (no esperar al listado)
  if (!isDesktop && routeId) {
    return <MeetingSessionPage key={String(routeId)} sessionId={routeId} />;
  }

  const listBody = loading ? (
    <Paper
      elevation={0}
      sx={{
        ...meetingListPaneSx,
        ...dashGlassCardSx(theme),
        display: "grid",
        placeItems: "center",
        minHeight: 160,
      }}
    >
      <CircularProgress size={28} />
    </Paper>
  ) : treeClients.length === 0 && rows.length === 0 ? (
    <Paper sx={dashGlassCardSx(theme)} elevation={0}>
      <Typography color="text.secondary">{t("Meetings.empty")}</Typography>
      <Button
        fullWidth={isNarrow}
        variant="contained"
        sx={{ mt: 2, minHeight: 48 }}
        startIcon={<AddOutlinedIcon />}
        onClick={openCreate}
      >
        {t("Meetings.new")}
      </Button>
    </Paper>
  ) : groups.length === 0 ? (
    <Paper sx={dashGlassCardSx(theme)} elevation={0}>
      <Typography color="text.secondary">
        {t("Meetings.filterEmpty")}
      </Typography>
    </Paper>
  ) : (
    listPane
  );

  return (
    <Box sx={meetingPageShellSx(contentMargin, theme)}>
      <Box
        sx={{
          ...meetingStickyHeaderSx(theme),
          flexShrink: 0,
          pb: 1,
          boxShadow: "none",
          bgcolor: "transparent",
          backdropFilter: "none",
        }}
      >
        <Box
          sx={{
            ...dashHeroSx(theme),
            position: "relative",
            flexShrink: 0,
            mb: 0,
          }}
        >
          <Box
            sx={{
              px: { xs: 1.75, sm: 2.25 },
              py: { xs: 1.25, sm: 1.35 },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              useFlexGap
              sx={{ flexWrap: "wrap", rowGap: 1 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.1}
                sx={{ flex: "0 1 auto", minWidth: 0 }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha("#00b8d4", 0.14),
                    color: "#00b8d4",
                    boxShadow: `0 0 0 1px ${alpha("#00b8d4", 0.28)}, 0 6px 16px ${alpha("#00b8d4", 0.18)}`,
                    flexShrink: 0,
                  }}
                >
                  <MicNoneOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.85}
                  sx={{ minWidth: 0, flexWrap: "wrap", rowGap: 0.5 }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      lineHeight: 1.15,
                      letterSpacing: -0.25,
                      fontSize: { xs: "1.15rem", sm: "1.25rem" },
                      color: "#ff1f8f",
                    }}
                  >
                    {t("Meetings.title")}
                  </Typography>
                  <Tooltip title={retentionTooltip} arrow enterTouchDelay={0}>
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={
                        <InfoOutlinedIcon
                          sx={{ fontSize: "14px !important" }}
                        />
                      }
                      label={t("Meetings.retentionChip", {
                        days: MEETING_MEDIA_RETENTION_DAYS,
                      })}
                      sx={{
                        height: 22,
                        fontWeight: 600,
                        fontSize: "0.68rem",
                        color: "text.secondary",
                        bgcolor: alpha(theme.palette.text.primary, 0.03),
                        borderColor: alpha(theme.palette.divider, 0.9),
                        "& .MuiChip-label": { px: 0.7 },
                        "& .MuiChip-icon": {
                          ml: 0.5,
                          color: "text.secondary",
                        },
                      }}
                    />
                  </Tooltip>
                </Stack>
              </Stack>

              {filtersBar}

              <Button
                variant="contained"
                size="small"
                startIcon={
                  <AddOutlinedIcon sx={{ fontSize: "18px !important" }} />
                }
                onClick={openCreate}
                sx={[
                  (th) => dashToolbarBtnSx(th, { primary: true }),
                  {
                    flexShrink: 0,
                    ml: { md: "auto" },
                    height: 34,
                    fontSize: "0.8125rem",
                    whiteSpace: "nowrap",
                  },
                ]}
              >
                {t("Meetings.new")}
              </Button>
            </Stack>
          </Box>
        </Box>

        {error ? (
          <Alert
            severity="error"
            sx={{ mt: 1.25, mb: 0 }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        ) : null}
      </Box>

      {isDesktop ? (
        <Box sx={meetingListSplitSx(treeCollapsed, captureParked)}>
          {listBody}
          {detailPane}
        </Box>
      ) : loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        listBody
      )}

      <Dialog
        open={open}
        onClose={() => !saving && setOpen(false)}
        fullWidth
        fullScreen={isNarrow}
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>{t("Meetings.new")}</DialogTitle>
        <DialogContent dividers={isNarrow} sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Stack spacing={1.5} sx={{ pt: 0.5, minWidth: 0 }}>
            <MeetingBrandSelect
              autoFocus
              value={customerName}
              onChange={(name) => {
                setCustomerName(name);
                setClientContactIds([]);
              }}
              disabled={saving}
            />
            <TextField
              type="date"
              label={t("Meetings.date")}
              fullWidth
              value={meetingDate}
              onChange={(e) => setMeetingDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={saving}
            />
            <MeetingAttendeeSelect
              value={attendeeIds}
              onChange={setAttendeeIds}
              disabled={saving}
            />
            <MeetingClientContactSelect
              brandName={customerName}
              value={clientContactIds}
              onChange={setClientContactIds}
              disabled={saving}
            />
            <TextField
              label={t("Meetings.sessionTitle")}
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("Meetings.titlePlaceholder")}
            />
            <MeetingConsentPanel
              value={consent}
              onChange={setConsent}
              disabled={saving}
              dense
            />
            <Alert severity="info">
              {t("Meetings.retentionBanner", {
                days: MEETING_MEDIA_RETENTION_DAYS,
                warnMb: formatMeetingMediaWarnMb(),
                photoMb: formatMeetingPhotoMaxMb(),
                videoMb: formatMeetingVideoMaxMb(),
              })}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 1,
            "& > :not(style)": { m: 0 },
          }}
        >
          <Button
            fullWidth={isNarrow}
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            {t("Meetings.cancel")}
          </Button>
          <Button
            fullWidth={isNarrow}
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !isMeetingConsentComplete(consent)}
            sx={{ minHeight: 48 }}
          >
            {saving ? <CircularProgress size={18} /> : t("Meetings.start")}
          </Button>
        </DialogActions>
      </Dialog>

      <MeetingDeleteDialog
        open={Boolean(deleteTarget)}
        session={deleteTarget}
        deleting={deleting}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </Box>
  );
}
